// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { HardfinTabCardWrapper } from '@/components/hardfin-tab-card-wrapper';
import { NotFoundErrorBoundary } from '@/components/not-found-error-boundary';
import { AccessTime, PostAddRounded } from '@/constants/hardfin-standard-icons';
import { CostDialog } from '@/costs/components/cost-dialog';
import { TimeRecordDialog } from '@/costs/components/time-record-dialog';
import { useDisclosure } from '@/hooks/use-disclosure';
import { getProject } from '@/project/api/get-project';
import { getProjectCosts } from '@/project/api/get-project-costs';
import { ProjectCostSummary } from '@/project/components/project-cost-summary';
import { ProjectCostTable } from '@/project/components/project-cost-table';
import { Box, Button, Stack, Tab, Tabs } from '@mui/material';
import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { SyntheticEvent, useState } from 'react';

export const Route = createFileRoute('/_app/projects/$projectKey/costs')({
  component: ProjectCostsRoute,
});

function ProjectCostsRoute() {
  const { projectKey } = Route.useParams();

  return (
    <NotFoundErrorBoundary
      entityType="project"
      listPath="/projects"
      listLabel="Go to projects"
    >
      <ProjectCostsContent projectKey={projectKey} />
    </NotFoundErrorBoundary>
  );
}

function ProjectCostsContent(props: { projectKey: string }) {
  const costDialog = useDisclosure();
  const timeCostDialog = useDisclosure();
  const [selectedTab, setSelectedTab] = useState(0);

  const projectQuery = useSuspenseQuery({
    queryKey: ['project', props.projectKey],
    queryFn: () => getProject(props.projectKey),
  });

  const costsQuery = useSuspenseQuery({
    queryKey: ['project', props.projectKey, 'cost'],
    queryFn: () => getProjectCosts(props.projectKey),
  });

  const handleTabChange = (_event: SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };

  return (
    <Stack>
      <CostDialog
        initialProject={projectQuery.data.project}
        isOpen={costDialog.isOpen}
        onClose={costDialog.close}
        invalidationKeys={[['project', props.projectKey], ['costs']]}
      />
      <TimeRecordDialog
        initialProject={projectQuery.data.project}
        isOpen={timeCostDialog.isOpen}
        onClose={timeCostDialog.close}
        invalidationKeys={[
          ['project', props.projectKey],
          ['costs'],
          ['time-record-aggregate'],
        ]}
      />

      <ProjectCostSummary projectId={projectQuery.data.project.id} />

      <HardfinTabCardWrapper
        title="Costs"
        actions={
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              startIcon={<AccessTime fontSize="small" />}
              onClick={timeCostDialog.open}
            >
              Add time
            </Button>
            <Button
              size="small"
              startIcon={<PostAddRounded fontSize="small" />}
              onClick={costDialog.open}
            >
              Add cost
            </Button>
          </Stack>
        }
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            '& .Mui-selected': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
            paddingLeft: 1,
            '& .MuiTab-root': {
              px: 2,
            },
            '& .MuiTab-root.MuiButtonBase-root': {
              ml: 0,
            },
          }}
        >
          <Tabs
            value={selectedTab}
            onChange={handleTabChange}
            indicatorColor="primary"
            textColor="primary"
            aria-label="Project cost filters"
          >
            <Tab label="All" />
            <Tab label="Hours" />
            <Tab label="Other" />
          </Tabs>
        </Box>

        <ProjectCostTable
          project={projectQuery.data.project}
          costs={costsQuery.data.data}
          selectedTab={selectedTab}
        />
      </HardfinTabCardWrapper>
    </Stack>
  );
}
